import React, { useState } from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { InputWithLabel } from "../../components/micro/input";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import {
  createBillingSetting,
  createGstSetting,
  getBillingSettingList,
} from "../../redux/masterSetting/slice";

const AddEditBillingModal = ({ onClose, isType, isMode, selectedData }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [formValues, setFormValues] = useState(null);

  const handleSubmit = async (values) => {
    setIsLoading(true);

    const payload = {
      email: values?.email,
      mobile: values?.phone,
      address: values?.address,
      postal_code: values?.postalcode,
    };
    const gstPayload = {
      gst: values?.gst,
    };

    const action = selectedData?.id
      ? createBillingSetting({ id: selectedData?.id, ...payload })
      : createBillingSetting(payload);

    const gstAction = selectedData?.id
      ? createGstSetting({ id: selectedData?.id, ...gstPayload })
      : createGstSetting(gstPayload);

    try {
      if (isMode === "gst") {
        const res = await dispatch(gstAction);
        if (res.type === "createGstSetting/fulfilled") {
          dispatch(getBillingSettingList());
        }
      } else {
        const res = await dispatch(action);
        if (res.type === "createBillingSetting/fulfilled") {
          dispatch(getBillingSettingList());
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
      onClose();
    }
  };

  const handleEditClick = () => {
    setShowConfirmationModal(true);
  };

  return (
    <>
      <div className="model" onClick={onClose}>
        <div
          className="bg-white items-center justify-center drop-shadow-lg rounded-lg w-full max-w-3xl py-8 px-6 mx-auto relative"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <h4 className="font-Montserrat text-[34px] pb-3 font-normal">
            {isMode === "billing"
              ? isType === "add"
                ? "Add Billing Contract"
                : "Edit Billing Contract"
              : isType === "add"
              ? "Add GST Percentage"
              : "Edit GST Percentage"}
          </h4>

          <Formik
            enableReinitialize={true}
            initialValues={{
              gst: selectedData?.gst || "",
              email: selectedData?.email || "",
              phone: selectedData?.mobile || "",
              address: selectedData?.address || "",
              postalcode: selectedData?.postal_code || "",
            }}
            validationSchema={Yup.object().shape({
              gst: Yup.string().trim().required("Please enter GST"),
              email: Yup.string()
                .trim()
                .email("Invalid email format")
                .required("Please enter email"),
              phone: Yup.string()
                .trim()
                .required("Please enter mobile number")
                .min(8, "Mobile is too short - should be 8 chars minimum.")
                .max(15, "Mobile is too long - should be 15 chars maximum.")
                .matches(/^\d+$/, "Mobile number must be numeric"),
              address: Yup.string()
                .trim()
                .min(3, "Address must be at least 3 characters")
                .required("Please enter address"),
              postalcode: Yup.string()
                .required("Please enter postal code")
                .length(6, "Postal code must be exactly 6 digit")
                .matches(/^\d+$/, "Postal code must be numeric"),
            })}
            onSubmit={(values) => {
              setFormValues(values);
              handleEditClick();
            }}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              setFieldValue,
            }) => {
              const cleanAddress = (address) =>
                (address || "")
                  .replace(/\s+/g, " ")
                  .replace(/\s*,\s*/g, ", ")
                  .trim();

              const handleAddressBlur = (e) => {
                const cleaned = cleanAddress(e.target.value);
                setFieldValue("address", cleaned);
                handleBlur(e);
              };
              return (
                <Form>
                  <div className="grid grid-cols-2 gap-6 mb-4 mt-3">
                    {isMode === "billing" ? (
                      <>
                        {/* Phone */}
                        <InputWithLabel
                          className={`${
                            errors.phone && touched.phone
                              ? `border border-red`
                              : `border border-borderTheme/80 focus-within:border-themeBtn `
                          }  transition duration-300 ease-in-out`}
                          idFromName="phone"
                          label="Mobile Number*"
                          type={"number"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.phone}
                          errors={errors.phone && touched.phone}
                          errorsText={errors.phone}
                          onFocus={(e) =>
                            e.target.addEventListener(
                              "wheel",
                              function (e) {
                                e.preventDefault();
                              },
                              { passive: false }
                            )
                          }
                        />
                        {/* Email */}
                        <InputWithLabel
                          className={`${
                            errors.email && touched.email
                              ? `border border-red`
                              : `border border-borderTheme/80 focus-within:border-themeBtn `
                          }  transition duration-300 ease-in-out`}
                          idFromName="email"
                          label="Email*"
                          type="email"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.email}
                          errors={errors.email && touched.email}
                          errorsText={errors.email}
                        />
                        {/* Address */}
                        <InputWithLabel
                          className={`${
                            errors.address && touched.address
                              ? `border border-red`
                              : `border border-borderTheme/80 focus-within:border-themeBtn `
                          } transition duration-300 ease-in-out`}
                          idFromName="address"
                          label="Address*"
                          type="address"
                          onChange={handleChange}
                          onBlur={handleAddressBlur}
                          value={values.address}
                          errors={errors.address && touched.address}
                          errorsText={errors.address}
                        />
                        {/* Postal Code */}
                        <InputWithLabel
                          className={`${
                            errors.postalcode && touched.postalcode
                              ? `border border-red`
                              : `border border-borderTheme/80 focus-within:border-themeBtn `
                          }  transition duration-300 ease-in-out`}
                          idFromName="postalcode"
                          label="Postal Code*"
                          type="number"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.postalcode}
                          errors={errors.postalcode && touched.postalcode}
                          errorsText={errors.postalcode}
                          onFocus={(e) =>
                            e.target.addEventListener(
                              "wheel",
                              function (e) {
                                e.preventDefault();
                              },
                              { passive: false }
                            )
                          }
                        />
                      </>
                    ) : (
                      <>
                        {/* Postal Code */}
                        <InputWithLabel
                          className={`${
                            errors.gst && touched.gst
                              ? `border border-red`
                              : `border border-borderTheme/80 focus-within:border-themeBtn `
                          }  transition duration-300 ease-in-out`}
                          idFromName="gst"
                          label="GST Amount(%)*"
                          type="number"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          onFocus={(e) =>
                            e.target.addEventListener(
                              "wheel",
                              function (e) {
                                e.preventDefault();
                              },
                              { passive: false }
                            )
                          }
                          value={values.gst}
                          errors={errors.gst && touched.gst}
                          errorsText={errors.gst}
                        />
                      </>
                    )}
                  </div>

                  {/* Submit button */}
                  <div className="flex justify-end gap-3 mt-6 w-full">
                    <button
                      onClick={onClose}
                      type="button"
                      className=" text-themeBtn w-full flex items-center uppercase font-Montserrat justify-center px-6 py-1.5 rounded border border-themeBtn"
                    >
                      <span>Cancel</span>
                    </button>
                    <button
                      type="submit"
                      className="flex items-center w-full justify-center px-6 py-1.5 rounded font-Montserrat uppercase border-2 border-themeBtn  text-white bg-themeBtn"
                    >
                      <span>Confirm</span>
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
        {showConfirmationModal && (
          <div>
            <ConfirmationModel
              confirmText={"Edit"}
              onClose={() => setShowConfirmationModal(false)}
              onConfirm={() => handleSubmit(formValues)}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default AddEditBillingModal;
